import { Injectable, inject } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import { Observable, of, switchMap, catchError, map } from 'rxjs';
import { AuthService } from '../service/auth.service';
import { Role } from '../enums/role';

@Injectable({
  providedIn: 'root',
})
export class NoAuthGuard {
  private authService = inject(AuthService);
  private router = inject(Router);

  canActivate(): Observable<boolean | UrlTree> {
    return this.checkNotAuthenticated();
  }

  canActivateChild(): Observable<boolean | UrlTree> {
    return this.checkNotAuthenticated();
  }

  private checkNotAuthenticated(): Observable<boolean | UrlTree> {
    // Si aucun token → accès permis
    if (!this.authService.getToken()) {
      return of(true);
    }

    // Si token présent, vérifie son état
    return this.authService.isAuthenticated().pipe(
      switchMap((authenticated) => {
        if (!authenticated && this.authService.getRefreshToken()) {
          // Token expiré mais refreshToken disponible
          return this.handleTokenRefresh();
        }
        if (!authenticated) return of(true);
        // Si authentifié → vérification du rôle
        return this.checkUserRole();
      }),
      catchError(() => {
        return of(true); // En cas d'erreur, on considère comme non authentifié
      }),
    );
  }

  private handleTokenRefresh(): Observable<boolean | UrlTree> {
    return this.authService.refreshToken().pipe(
      switchMap(({ token }) => {
        this.authService.saveToken(token);
        // Après rafraîchissement, on vérifie le rôle
        return this.checkUserRole();
      }),
      catchError(() => {
        this.authService.logout();
        return of(true); // Rafraîchissement échoué → accès permis
      }),
    );
  }

  private checkUserRole(): Observable<UrlTree> {
    // Cas 1: User déjà chargé
    if (this.authService.currentUser$()) {
      return of(this.redirectBasedOnRole());
    }

    // Cas 2: Besoin de charger le profil
    return this.authService.loadUserProfile().pipe(
      map(() => this.redirectBasedOnRole()),
      catchError(() => {
        this.authService.logout();
        return of(this.router.parseUrl('/login')); // Redirection vers login si erreur
      }),
    );
  }

  private redirectBasedOnRole(): UrlTree {
    const user = this.authService.currentUser$();
    return user.role === Role.admin
      ? this.router.parseUrl('/admin')
      : this.router.parseUrl('/apps/dashboard');
  }
}
